// product-page.js — THRUX product page interactivity.
//
// Hydrates BOTH buy box instances (hero + final-cta). Each buy box owns its
// own variant pill state, quantity tier state, per-can picker state, dynamic
// CTA price, and form submit handler. When the user changes flavor on either
// buy box, the hero gallery's primary image swaps to the matching variant
// mockup.
//
// State is local to each buy box (so the user can configure differently in
// the two boxes if they want), with one exception: the hero gallery image
// reflects whichever buy box was touched last. This is intentional — both
// boxes are conversion endpoints, but the gallery image is a global asset.
//
// Cart submit path:
//   1. Build a payload from the active mode (single-flavor or per-can).
//      Single: {id, quantity}. Multi: {items: [{id, quantity}, ...]} grouped
//      by variant. Multi posts via /cart/add.js's items[] batch format, same
//      pattern thrux-cart-cross-sell.js already uses.
//   2. POST to /cart/add.js (JSON).
//   3. On success: GET /cart.js to refresh state, dispatch a
//      bubbling `cart:update` CustomEvent with the cart object as detail.
//      Horizon's cart drawer auto-opens via its CartAddEvent listener
//      (cart-drawer.js #handleCartAdd) when it sees the cart:update event.
//   4. On error: log + fall back to native form submit (full /cart redirect).
//
// Mode switching:
//   - data-mode="single" on the buy box: qty=1, single flavor selector visible,
//     per-can picker hidden. Submit posts a single line item.
//   - data-mode="multi" on the buy box: qty>1, per-can picker visible (rows
//     0..qty-1), single flavor selector hidden. Submit posts a multi-line
//     items[] payload grouped by variant.
//
//   On 1→N transition: all newly-visible rows default to the current
//   single-flavor selection (so a customer who picked REDLINE then bumped
//   from 1 to 5 cans gets 5 REDLINE rows, not 5 CIDER rows).
//   On N→1 transition: the single flavor selector adopts row 0's selection.
//
// Discounts: tier prices are display-only (computed in Liquid from the
// live base price + locked per-can rates $9.99 / $9.49 / $8.99). The
// actual line-item discount fires server-side via Shopify automatic
// discounts (per-can tier rules — see recreate_thrux_tier_discounts.mjs).
// The buy box shows the post-discount price; the cart line items show
// full unit price minus the auto discount.

(function () {
  'use strict';

  function init() {
    var buyBoxes = document.querySelectorAll('[data-buy-box]');
    buyBoxes.forEach(hydrate);

    // After both boxes are hydrated, sync the gallery image to the default
    // variant (whichever pill is initially selected on the hero buy box).
    syncGalleryToCurrent();
  }

  function hydrate(buyBox) {
    if (buyBox.dataset.productReady === 'true') return;
    buyBox.dataset.productReady = 'true';
    var variantPills = buyBox.querySelectorAll('[data-variant-pill]');
    var tierButtons = buyBox.querySelectorAll('[data-tier]');
    var perCanRows = buyBox.querySelectorAll('[data-per-can-row]');
    var form = buyBox.querySelector('[data-buy-box-form]');
    var variantInput = buyBox.querySelector('[data-buy-box-variant-input]');
    var qtyInput = buyBox.querySelector('[data-buy-box-qty-input]');
    var ctaPrice = buyBox.querySelector('[data-buy-box-cta-price]');
    var cta = buyBox.querySelector('[data-buy-box-cta]');
    var planInput = buyBox.querySelector('[data-buy-box-plan-input]');
    var ptypeToggle = buyBox.querySelector('[data-ptype-toggle]');

    // This hidden mirror is the value sent to GA4. The selected selling plan
    // must change it along with the visible subscription tier price.
    function syncCtaPrice(tier) {
      tier = tier || buyBox.querySelector('[data-tier][data-selected]');
      if (!ctaPrice || !tier) return;
      var subPrice = tier.querySelector('.product-buy-box__tier-sub');
      var subscribed = buyBox.getAttribute('data-purchase-type') === 'subscribe';
      ctaPrice.textContent = subscribed && subPrice
        ? subPrice.firstChild.textContent.trim()
        : '$' + tier.dataset.price;
    }

    // Top-row flavor pills. Behavior is mode-aware.
    //
    // single mode (qty=1): same as before — selects the single line's
    //   variant, updates the hidden input, swaps the gallery image.
    //
    // multi mode (qty>1): the pill is a MASS-ACTION SHORTCUT. Clicking
    //   CIDER on the top row fills every visible per-can row with CIDER.
    //   The pill itself does NOT carry a [data-selected] state in multi
    //   mode because there's no "single variant" to select — the picker
    //   below owns per-can state. The gallery still swaps so the visual
    //   anchors to the last-touched flavor.
    variantPills.forEach(function (pill) {
      pill.addEventListener('click', function () {
        if (pill.disabled) return;
        var mode = buyBox.getAttribute('data-mode') || 'single';
        var flavor = pill.dataset.variantFlavor;
        swapGalleryToVariant(pill.dataset.variantId, buyBox);

        if (mode === 'single') {
          variantPills.forEach(function (p) {
            p.removeAttribute('data-selected');
            p.setAttribute('aria-checked', 'false');
          });
          pill.setAttribute('data-selected', '');
          pill.setAttribute('aria-checked', 'true');
          if (variantInput) variantInput.value = pill.dataset.variantId;
          return;
        }

        // Mass-fill visible rows with this flavor.
        var visibleRows = buyBox.querySelectorAll('[data-per-can-row]:not([hidden])');
        visibleRows.forEach(function (row) {
          setRowFlavor(row, flavor);
        });
      });
    });

    // Per-can chip clicks. Each row is its own radiogroup — selecting one
    // chip clears the other two in the same row only.
    perCanRows.forEach(function (row) {
      var chips = row.querySelectorAll('[data-per-can-chip]');
      chips.forEach(function (chip) {
        chip.addEventListener('click', function () {
          if (chip.getAttribute('aria-disabled') === 'true') return;
          chips.forEach(function (c) {
            c.removeAttribute('data-selected');
            c.setAttribute('aria-checked', 'false');
          });
          chip.setAttribute('data-selected', '');
          chip.setAttribute('aria-checked', 'true');
          swapGalleryToVariant(chip.dataset.variantId, buyBox);
        });
      });
    });

    // Quantity tier clicks. Drives the mode transition + upgrade-nudge visibility.
    tierButtons.forEach(function (tier) {
      tier.addEventListener('click', function () {
        tierButtons.forEach(function (t) {
          t.removeAttribute('data-selected');
          t.setAttribute('aria-checked', 'false');
        });
        tier.setAttribute('data-selected', '');
        tier.setAttribute('aria-checked', 'true');
        var newQty = parseInt(tier.dataset.qty, 10);
        if (qtyInput) qtyInput.value = newQty;
        syncCtaPrice(tier);
        applyMode(buyBox, newQty);
        applyUpgradeNudge(buyBox, newQty);
        seedOneOfEach(buyBox, tier);
      });
    });

    // Upgrade nudge — only renders at qty=2, jumps to the founders qty=3
    // tier (founders era) or the 5-can tier (default era). Programmatic
    // click on the target tier reuses the existing tier-click side
    // effects (mode swap, CTA price, per-can visibility) — no parallel
    // code path to maintain.
    var nudge = buyBox.querySelector('[data-upgrade-nudge]');
    if (nudge) {
      var nudgeCta = nudge.querySelector('[data-upgrade-nudge-jump]');
      if (nudgeCta) {
        nudgeCta.addEventListener('click', function () {
          var targetQty = nudge.dataset.nudgeTargetQty;
          var targetTier = buyBox.querySelector('[data-tier][data-qty="' + targetQty + '"]');
          if (targetTier) targetTier.click();
        });
      }
    }

    // Purchase-type toggle (subscribe / one-time). Flips data-purchase-type on
    // the buy box (CSS swaps the Option B price emphasis) and enables/disables
    // the hidden selling_plan input so the single-flavor FormData path posts
    // (or omits) the subscription. The multi-flavor path reads
    // data-purchase-type in collectMultiItems.
    if (ptypeToggle) {
      var ptypeOpts = ptypeToggle.querySelectorAll('[data-ptype]');
      ptypeOpts.forEach(function (opt) {
        opt.addEventListener('click', function () {
          var type = opt.dataset.ptype;
          ptypeOpts.forEach(function (o) {
            o.removeAttribute('data-selected');
            o.setAttribute('aria-checked', 'false');
          });
          opt.setAttribute('data-selected', '');
          opt.setAttribute('aria-checked', 'true');
          buyBox.setAttribute('data-purchase-type', type);
          if (planInput) planInput.disabled = (type !== 'subscribe');
          syncCtaPrice();
        });
      });
    }

    // Sync default tier state on load. Without this, the Liquid-rendered
    // fallback CTA text could drift from the selected tier if the snippet's
    // hardcoded fallback hasn't been updated in lockstep.
    var defaultTier = buyBox.querySelector('[data-tier][data-default="true"]');
    if (defaultTier) {
      defaultTier.setAttribute('data-selected', '');
      defaultTier.setAttribute('aria-checked', 'true');
      var defaultQty = parseInt(defaultTier.dataset.qty, 10);
      if (qtyInput) qtyInput.value = defaultQty;
      syncCtaPrice(defaultTier);
      applyMode(buyBox, defaultQty);
      applyUpgradeNudge(buyBox, defaultQty);
      seedOneOfEach(buyBox, defaultTier);
    }

    if (form) form.addEventListener('submit', onSubmit);

    function onSubmit(e) {
      e.preventDefault();
      if (!cta || cta.disabled) return;

      var mode = buyBox.getAttribute('data-mode') || 'single';
      var qty = parseInt(qtyInput?.value || '1', 10);
      var payload;
      var headers = { 'X-Requested-With': 'XMLHttpRequest', 'Accept': 'application/json' };
      var body;

      // Bundle the REAL cart section IDs with the add (the way
      // product-form.js does) so the response carries fresh drawer markup.
      // The previous literals ('cart-drawer,cart-icon-bubble') were Dawn
      // section names that don't exist in this theme — Shopify returned
      // nulls and the drawer needed a second round-trip to update.
      var cartSectionIds = [];
      document.querySelectorAll('cart-items-component').forEach(function (el) {
        if (el.dataset.sectionId) cartSectionIds.push(el.dataset.sectionId);
      });
      var sectionsParam = cartSectionIds.join(',');

      if (mode === 'multi') {
        var items = collectMultiItems(buyBox);
        if (!items.length) {
          // Shouldn't happen — rows always default to CIDER — but degrade
          // gracefully to single-flavor add if the picker is somehow empty.
          payload = { id: variantInput?.value, quantity: qty };
          headers['Content-Type'] = 'application/x-www-form-urlencoded';
          body = new URLSearchParams(payload).toString();
        } else {
          headers['Content-Type'] = 'application/json';
          body = JSON.stringify({ items: items, sections: sectionsParam });
        }
      } else {
        // Single-flavor path: post the form as URL-encoded (matches the
        // pre-existing behavior, no payload shape change).
        var fd = new FormData(form);
        fd.append('sections', sectionsParam);
        cta.disabled = true;
        cta.classList.add('is-loading');
        fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'X-Requested-With': 'XMLHttpRequest', 'Accept': 'application/javascript' },
          body: fd,
        })
          .then(handleAddResponse)
          .then(
            function (added) {
              // Add succeeded. Failures from here on must NOT trigger the
              // native form fallback — re-posting the form would add the
              // items a second time. Degrade to showing the cart instead.
              return refreshCart()
                .then(function (cart) { dispatchCartUpdate(cart, form, qty, variantInput?.value, added && added.sections); })
                .catch(function (err) {
                  console.warn('[thrux] add succeeded but cart refresh failed; redirecting to /cart', err);
                  window.location.assign('/cart');
                });
            },
            function (err) {
              // The add itself failed — the form's hidden inputs hold the
              // same single variant/quantity we just posted, so a native
              // re-submit is safe here (and only here).
              fallbackSubmit(err, form, onSubmit);
            }
          )
          .finally(function () { cta.disabled = false; cta.classList.remove('is-loading'); });
        return;
      }

      cta.disabled = true;
      cta.classList.add('is-loading');

      fetch('/cart/add.js', { method: 'POST', headers: headers, body: body })
        .then(handleAddResponse)
        .then(
          function (added) {
            // Add succeeded — never native-resubmit past this point (it
            // would double-add). Degrade to showing the cart instead.
            return refreshCart()
              .then(function (cart) { dispatchCartUpdate(cart, form, qty, variantInput?.value, added && added.sections); })
              .catch(function (err) {
                console.warn('[thrux] add succeeded but cart refresh failed; redirecting to /cart', err);
                window.location.assign('/cart');
              });
          },
          function (err) {
            if (items.length) {
              // Multi-flavor add failed. The form's hidden id/quantity hold
              // stale single-mode values, so a native re-submit would post
              // the WRONG line items (qty × one variant instead of the
              // per-can mix). Leave the cart untouched; the button
              // re-enables for a retry.
              console.warn('[thrux] multi-flavor add failed; cart unchanged', err);
            } else {
              // Degraded single-style payload — hidden inputs match what we
              // posted, so the native fallback is safe.
              fallbackSubmit(err, form, onSubmit);
            }
          }
        )
        .finally(function () { cta.disabled = false; cta.classList.remove('is-loading'); });
    }
  }

  function applyMode(buyBox, qty) {
    // Waitlist phase doesn't render the per-can fieldset. If no rows exist,
    // leave the mode flag alone so the single-flavor pills stay visible
    // (the NEON "FOUNDERS SECRET" brand moment depends on the pills being
    // shown even when the buy box can't submit).
    var perCanRows = buyBox.querySelectorAll('[data-per-can-row]');
    if (perCanRows.length === 0) return;

    var newMode = qty > 1 ? 'multi' : 'single';
    var oldMode = buyBox.getAttribute('data-mode') || 'single';
    buyBox.setAttribute('data-mode', newMode);

    if (newMode === 'multi') {
      // Capture the current selected-pill flavor BEFORE clearing — we
      // need it for the single→multi seed below.
      var selectedPill = buyBox.querySelector('[data-variant-pill][data-selected]');
      var pillFlavor = selectedPill ? selectedPill.dataset.variantFlavor : null;
      // Multi mode invariant: top pills must NOT carry a [data-selected]
      // state — in this mode they're mass-action shortcuts (click CIDER
      // to set every visible row to CIDER), not single-selection ring
      // state. Clearing this on every multi-entry (not just on the
      // single→multi transition) handles the initial-load case where
      // Liquid pre-rendered CIDER as data-selected against the default
      // 5-can tier.
      var pills = buyBox.querySelectorAll('[data-variant-pill]');
      pills.forEach(function (p) {
        p.removeAttribute('data-selected');
        p.setAttribute('aria-checked', 'false');
      });
      // On a true single→multi transition, seed row 0 from the
      // previously-selected single pill flavor so the user's qty=1 pick
      // carries into can 1 of the pack.
      if (oldMode === 'single' && pillFlavor) {
        var row0 = buyBox.querySelector('[data-per-can-row][data-row-index="0"]');
        if (row0) setRowFlavor(row0, pillFlavor);
      }
    } else if (oldMode === 'multi' && newMode === 'single') {
      // multi → single. Adopt row 0's selection into the single pill state
      // so qty=1 reflects the variety pack's first can.
      var firstRow = buyBox.querySelector('[data-per-can-row][data-row-index="0"]');
      if (firstRow) {
        var selectedChip = firstRow.querySelector('[data-per-can-chip][data-selected]');
        if (selectedChip) {
          var pills = buyBox.querySelectorAll('[data-variant-pill]');
          pills.forEach(function (p) {
            var match = p.dataset.variantFlavor === selectedChip.dataset.flavor;
            if (match) {
              p.setAttribute('data-selected', '');
              p.setAttribute('aria-checked', 'true');
              var variantInput = buyBox.querySelector('[data-buy-box-variant-input]');
              if (variantInput) variantInput.value = p.dataset.variantId;
            } else {
              p.removeAttribute('data-selected');
              p.setAttribute('aria-checked', 'false');
            }
          });
        }
      }
    }

    // Visibility: rows 0..qty-1 visible, the rest hidden. Rows past qty
    // retain their flavor state in DOM — so a 10 → 5 → 10 transition
    // restores the user's prior cans 6..10 selections intact.
    perCanRows.forEach(function (row) {
      var idx = parseInt(row.dataset.rowIndex, 10);
      if (idx < qty) {
        row.removeAttribute('hidden');
      } else {
        row.setAttribute('hidden', '');
      }
    });
  }

  function applyUpgradeNudge(buyBox, qty) {
    // Show the upgrade nudge only at qty=2. The Liquid renders one
    // variant of the nudge (founders or fill_5) based on the launch
    // state; JS just toggles visibility.
    var nudge = buyBox.querySelector('[data-upgrade-nudge]');
    if (!nudge) return;
    if (qty === 2) {
      nudge.setAttribute('data-active', '');
    } else {
      nudge.removeAttribute('data-active');
    }
  }

  // "One of each flavor" seeding (founders/3-pack promise): rows 0..N pick
  // chip 0..N in order, so a 3-can pack defaults to CIDER + REDLINE + NEON.
  // Only fires on tiers carrying data-founders-mix; any row stays fully
  // overridable by the customer afterward.
  function seedOneOfEach(buyBox, tier) {
    if (!tier || !tier.hasAttribute('data-founders-mix')) return;
    var rows = buyBox.querySelectorAll('[data-per-can-row]:not([hidden])');
    rows.forEach(function (row, i) {
      var chips = row.querySelectorAll('[data-per-can-chip]');
      var chip = chips[i % chips.length];
      if (!chip || chip.getAttribute('aria-disabled') === 'true') return;
      chips.forEach(function (c) {
        c.removeAttribute('data-selected');
        c.setAttribute('aria-checked', 'false');
      });
      chip.setAttribute('data-selected', '');
      chip.setAttribute('aria-checked', 'true');
    });
  }

  function setRowFlavor(row, flavor) {
    var chips = row.querySelectorAll('[data-per-can-chip]');
    chips.forEach(function (chip) {
      var match = chip.dataset.flavor === flavor;
      if (match) {
        chip.setAttribute('data-selected', '');
        chip.setAttribute('aria-checked', 'true');
      } else {
        chip.removeAttribute('data-selected');
        chip.setAttribute('aria-checked', 'false');
      }
    });
  }

  function collectMultiItems(buyBox) {
    var rows = buyBox.querySelectorAll('[data-per-can-row]:not([hidden])');
    var counts = {};
    rows.forEach(function (row) {
      var chip = row.querySelector('[data-per-can-chip][data-selected]');
      if (!chip) return;
      var id = chip.dataset.variantId;
      counts[id] = (counts[id] || 0) + 1;
    });
    var subscribe = buyBox.getAttribute('data-purchase-type') === 'subscribe';
    var planId = buyBox.getAttribute('data-subscribe-plan');
    return Object.keys(counts).map(function (id) {
      var item = { id: id, quantity: counts[id] };
      if (subscribe && planId) item.selling_plan = planId;
      return item;
    });
  }

  function handleAddResponse(r) {
    if (!r.ok) throw new Error('cart/add failed: ' + r.status);
    return r.json();
  }

  function refreshCart() {
    return fetch('/cart.js', { headers: { 'Accept': 'application/json' } }).then(function (r) { return r.json(); });
  }

  function dispatchCartUpdate(cart, form, qty, variantId, sections) {
    document.dispatchEvent(new CustomEvent('cart:update', {
      bubbles: true,
      detail: {
        resource: cart,
        sourceId: form?.id,
        data: { source: 'product-page-buy-box', itemCount: cart.item_count, sections: sections },
      },
    }));
    if (window.Shopify?.customerPrivacy?.analyticsProcessingAllowed?.() === true && typeof window.gtag === 'function') {
      var ctaPriceEl = form?.querySelector('[data-buy-box-cta-price]');
      window.gtag('event', 'add_to_cart', {
        currency: 'USD',
        value: parseFloat(ctaPriceEl?.textContent?.replace('$', '') || '0'),
        items: [{ item_id: variantId, quantity: qty }],
      });
    }
  }

  function fallbackSubmit(err, form, onSubmitHandler) {
    console.warn('[thrux] buy box submit failed, falling back to /cart redirect', err);
    if (form) {
      form.removeEventListener('submit', onSubmitHandler);
      form.submit();
    }
  }

  function swapGalleryToVariant(variantId, buyBox) {
    var mapEl = buyBox.querySelector('[data-buy-box-variant-images]');
    if (!mapEl) return;
    var entry;
    try {
      entry = JSON.parse(mapEl.textContent)[variantId];
    } catch (e) {
      // Map missing or malformed; leave gallery + note as-is.
      return;
    }
    if (!entry) return;

    // Gallery primary image swap. Skip gracefully if the target isn't present
    // (e.g. reduced-motion layout variations) but still update the note below.
    var galleryImg = document.querySelector('[data-variant-media-target]');
    if (galleryImg && entry.url) {
      galleryImg.src = entry.url;
      galleryImg.removeAttribute('srcset');
      if (entry.alt) galleryImg.alt = entry.alt;
    }

    // REDLINE heads-up note follows the anchored flavor (single global element,
    // same as the gallery image; reflects whichever buy box was touched last).
    toggleRedlineNote(entry.flavor);
  }

  function toggleRedlineNote(flavor) {
    var note = document.querySelector('[data-redline-note]');
    if (!note) return;
    if (flavor === 'redline') {
      note.removeAttribute('hidden');
    } else {
      note.setAttribute('hidden', '');
    }
  }

  function syncGalleryToCurrent() {
    var heroBox = document.querySelector('[data-buy-box][data-instance="hero"]');
    if (!heroBox) return;
    var mode = heroBox.getAttribute('data-mode') || 'single';
    if (mode === 'multi') {
      // Use row 0's selection as the gallery anchor on initial render.
      var firstRowChip = heroBox.querySelector('[data-per-can-row][data-row-index="0"] [data-per-can-chip][data-selected]');
      if (firstRowChip) {
        swapGalleryToVariant(firstRowChip.dataset.variantId, heroBox);
        return;
      }
    }
    var selected = heroBox.querySelector('[data-variant-pill][data-selected]');
    if (!selected) return;
    swapGalleryToVariant(selected.dataset.variantId, heroBox);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  document.addEventListener('shopify:section:load', init);
})();
